import React, { useRef, useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { ApiService, CommitmentService } from "../../../../api";
import { CustomButton, icon, createNotification, CustomTooltip } from "../../../../common/components";
import { translate } from "../../../../common/providers";
import { CommitmentFilters } from "../index";
import styles from "./CommitmentHeader.module.css";
import { useApi } from "../../../../common/hooks";
import { downloadFile } from "../../../../common/utils";
import { buildFilterPayload } from "../../utils";

const CommitmentHeader = ({ hasNoCommitments, launchProgress, projectHasDocuments = false, onLaunch }) => {
	const [isLaunchAvailable, setIsLaunchAvailable] = useState(false);
	const [isExecutingAgent, setIsExecutingAgent] = useState(false);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const hasAvailableAIQuota = useSelector(({ context }) => context?.hasAvailableAIQuota);
	const appliedFilters = useSelector(({ commitmentTracking }) => commitmentTracking.appliedFilters);
	const filterDefinitions = useSelector(({ commitmentTracking }) => commitmentTracking.filterDefinitions);

	const { call: launchDetection } = useApi(CommitmentService.launchDetection);
	const { call: checkIsLaunchAvailable } = useApi(CommitmentService.isLaunchAvailable);
	const { call: exportCommitments } = useApi(CommitmentService.exportCommitments);

	const cancelTokenSourceRef = useRef(null);
	const buildSearchPayload = useCallback(
		(filterState) => buildFilterPayload(filterDefinitions, filterState),
		[filterDefinitions]
	);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		const checkLaunchAvailability = async () => {
			try {
				const response = await checkIsLaunchAvailable({ projectId });
				setIsLaunchAvailable(response?.value);
			} catch (error) {
				console.error("Error checking launch availability:", error);
				setIsLaunchAvailable(false);
			}
		};
		checkLaunchAvailability();
	}, [projectId, checkIsLaunchAvailable]);
	const handleLaunchDetection = async () => {
		try {
			if (!projectId) {
				throw new Error("ID du projet manquant");
			}
			setIsExecutingAgent(true);
			launchDetection({ projectId })
				.then(() => {
					createNotification({
						type: "success",
						message: translate("commitment-tracking.actions.launched-success"),
					});
					setIsLaunchAvailable(false);
					onLaunch();
				})
				.catch(console.error);
		} catch (error) {
			console.error("Error launching detection:", error);
			setIsExecutingAgent(false);
			createNotification({
				type: "error",
				message: error?.message || "An error occurred while starting the detection.",
			});
		}
	};
	const handleExport = () => {
		createNotification({
			type: "info",
			message: translate("commitment-tracking.header.export-in-progress"),
		});
		const payload = buildSearchPayload(appliedFilters);
		exportCommitments({ projectId }, payload)
			.then(({ data, filename }) => {
				downloadFile({ data, filename, filetype: "xlsx" });
			})
			.catch(console.error);
	};
	const displayDisableText = () => {
		if (!isLaunchAvailable) {
			return translate("commitment-tracking.banner.btn.launch-detection.tooltip.disabled.unavailable");
		}
		if (launchProgress) {
			return translate("commitment-tracking.banner.btn.launch-detection.tooltip.disabled.in-progress");
		}
		if (!projectHasDocuments) {
			return translate("commitment-tracking.banner.btn.launch-detection.tooltip.disabled.no-document");
		}
		if (!hasAvailableAIQuota) {
			return translate("common:company.quotas.consumption-reached.tooltip");
		}
		return "";
	};
	return (
		<div className={styles.header}>
			<div className={styles.filterSection}>
				<CommitmentFilters launchProgress={launchProgress} />
			</div>
			<div className={styles.actionButtons}>
				<CustomButton
					color="primary"
					disabled={!!launchProgress || hasNoCommitments}
					size="md"
					startIcon={icon.faUpload}
					variant="outlined"
					onClick={handleExport}
				>
					{translate("commitment-tracking.header.export")}
				</CustomButton>
				<CustomTooltip title={displayDisableText()}>
					<span>
						<CustomButton
							color="primary"
							disabled={
								!isLaunchAvailable ||
								!!launchProgress ||
								isExecutingAgent ||
								!projectHasDocuments ||
								!hasAvailableAIQuota
							}
							size="md"
							startIcon={icon.faWandMagic}
							variant="contained"
							onClick={handleLaunchDetection}
						>
							{translate("commitment-tracking.header.launch-detection")}
						</CustomButton>
					</span>
				</CustomTooltip>
			</div>
		</div>
	);
};

export default CommitmentHeader;
